import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accounts as accountsApi,
  manualOps,
  manualOpTemplates,
  reports,
  type Account,
  type ManualOperationEntry,
} from '@/api/documents';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { ProjectField } from '@/components/ProjectSelect';
import SearchSelect from '@/components/SearchSelect';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import FopExclusionRescueSection from '@/components/FopExclusionRescueSection';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { createThenPost } from '@/shared/utils/createThenPost';
import { copyRailAction, nowForDocDate, useDocCopy } from '@/shared/hooks/useDocCopy';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  operation_content: string;
  comment: string;
  project_id: string | null;
  entries: ManualOperationEntry[];
  // Фаза 2.1 #3: subtype для специфічних flows.
  operation_subtype: '' | 'batch_correction';
  related_batch_key_id: string;
  new_cost_price: string;
}

const NEW_ROW = (n: number): ManualOperationEntry => ({
  line_number: n, dt_account_id: '', cr_account_id: '', amount: '0', content: '',
});

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  operation_content: '',
  comment: '',
  project_id: null,
  entries: [NEW_ROW(1)],
  operation_subtype: '',
  related_batch_key_id: '',
  new_cost_price: '',
};

// Типи субконто, доступні у ручній проводці (значення = *_subconto_*_type).
const SUBCONTO_TYPES: { type: string; label: string }[] = [
  { type: 'ref.contractors', label: 'Контрагент' },
  { type: 'ref.contracts', label: 'Договір' },
  { type: 'ref.nomenclature', label: 'Номенклатура' },
  { type: 'ref.employees', label: 'Співробітник' },
  { type: 'ref.warehouses', label: 'Склад' },
  { type: 'ref.cost_items', label: 'Стаття витрат' },
];

// Підказка типів субконто за кодом рахунку (префікс-матч, специфічніший
// префікс перемагає). Як у 1С: обрав 361 — одразу слоти Контрагент+Договір.
const SUBCONTO_HINT: [string, string[]][] = [
  ['372', ['ref.employees']],
  ['36', ['ref.contractors', 'ref.contracts']],
  ['37', ['ref.contractors', 'ref.contracts']],
  ['63', ['ref.contractors', 'ref.contracts']],
  ['68', ['ref.contractors', 'ref.contracts']],
  ['66', ['ref.employees']],
  ['20', ['ref.nomenclature', 'ref.warehouses']],
  ['22', ['ref.nomenclature', 'ref.warehouses']],
  ['26', ['ref.nomenclature', 'ref.warehouses']],
  ['28', ['ref.nomenclature', 'ref.warehouses']],
  ['23', ['ref.cost_items']],
  ['91', ['ref.cost_items']],
  ['92', ['ref.cost_items']],
  ['93', ['ref.cost_items']],
  ['94', ['ref.cost_items']],
];

function hintForAccount(code: string | undefined): string[] {
  if (!code) return [];
  let best: string[] = [];
  let bestLen = 0;
  for (const [prefix, types] of SUBCONTO_HINT) {
    if (code.startsWith(prefix) && prefix.length > bestLen) {
      best = types; bestLen = prefix.length;
    }
  }
  return best;
}

/** Позначка обовʼязкового поля. */
const Req = () => <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>;

type Side = 'dt' | 'cr';
type SlotN = 1 | 2 | 3;

const slotKey = (side: Side, n: SlotN, part: 'type' | 'id') =>
  `${side}_subconto_${n}_${part}` as keyof ManualOperationEntry;

export default function ManualOperationEditor() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const docQuery = useQuery({
    queryKey: ['manual-op', id],
    queryFn: () => manualOps.get(id!),
    enabled: !isNew,
  });
  // ч.1268: «скопіювати» — зразок для нового документа (…/new?copy=<id>)
  const copy = useDocCopy(isNew, 'manual-op', (cid) => manualOps.get(cid));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.manual_operations', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  // ---- Довідники для субконто/валюти/рахунків ----------------------------
  const accountsQ = useQuery({
    queryKey: ['accounts', 'all'],
    queryFn: () => accountsApi.list({ only_postable: true, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const accountById = useMemo(() => {
    const m: Record<string, Account> = {};
    (accountsQ.data ?? []).forEach((a) => { m[a.id] = a; });
    return m;
  }, [accountsQ.data]);

  // Пошуковий вибір рахунку: label = код (вузьке поле), пошук і по назві (hint).
  const accountOptions = useMemo(
    () => (accountsQ.data ?? []).map((a) => ({
      id: a.id, label: a.code, hint: a.name,
    })),
    [accountsQ.data],
  );

  const ctrQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const nomQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const empQ = useQuery({
    queryKey: ['employees-all-mo', form.organization_id],
    queryFn: () => refs.employees(form.organization_id || undefined),
  });
  const whQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const ciQ = useQuery({ queryKey: ['cost-items'], queryFn: () => refs.costItems() });
  const curQ = useQuery({ queryKey: ['currencies'], queryFn: () => refs.currencies() });

  // ---- Пікер партії для batch_correction: залишки по партіях на дату документа.
  const [bcWarehouse, setBcWarehouse] = useState('');
  const [bcNom, setBcNom] = useState('');
  const batchesQ = useQuery({
    queryKey: ['goods-batches', form.organization_id, form.document_date.slice(0, 10)],
    queryFn: () => reports.goodsBatches({
      organization_id: form.organization_id,
      on_date: `${form.document_date.slice(0, 10)}T23:59:59`,
    }),
    enabled: !!form.organization_id && form.operation_subtype === 'batch_correction',
  });
  const bcRows = batchesQ.data?.rows ?? [];
  const bcWarehouses = useMemo(() => {
    const m = new Map<string, string>();
    bcRows.forEach((r) => m.set(r.warehouse_id, r.warehouse_name));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [bcRows]);
  const bcNoms = useMemo(() => {
    const m = new Map<string, string>();
    bcRows.filter((r) => r.warehouse_id === bcWarehouse)
      .forEach((r) => m.set(r.nomenclature_id, r.nomenclature_code ? `${r.nomenclature_name} — ${r.nomenclature_code}` : r.nomenclature_name));
    return Array.from(m, ([id, label]) => ({ id, label }));
  }, [bcRows, bcWarehouse]);
  const bcBatches = useMemo(
    () => bcRows.filter((r) => r.warehouse_id === bcWarehouse && r.nomenclature_id === bcNom),
    [bcRows, bcWarehouse, bcNom]);
  const bcSelected = bcRows.find((r) => r.batch_key_id === form.related_batch_key_id);
  // Відкриття збереженого документа з партією — відновити склад/номенклатуру пікера.
  useEffect(() => {
    if (form.related_batch_key_id && !bcWarehouse) {
      const r = bcRows.find((x) => x.batch_key_id === form.related_batch_key_id);
      if (r) { setBcWarehouse(r.warehouse_id); setBcNom(r.nomenclature_id); }
    }
  }, [bcRows, form.related_batch_key_id, bcWarehouse]);

  // Договори — per-contractor (контрагент того ж боку рядка).
  const contractorIdsInUse = useMemo(() => {
    const s = new Set<string>();
    form.entries.forEach((e) => {
      (['dt', 'cr'] as Side[]).forEach((side) => {
        ([1, 2, 3] as SlotN[]).forEach((n) => {
          if (e[slotKey(side, n, 'type')] === 'ref.contractors') {
            const v = e[slotKey(side, n, 'id')] as string | null | undefined;
            if (v) s.add(v);
          }
        });
      });
    });
    return Array.from(s);
  }, [form.entries]);
  const contractQueries = useQueries({
    queries: contractorIdsInUse.map((cid) => ({
      queryKey: ['contracts', form.organization_id, cid],
      queryFn: () => refs.contracts({
        organization_id: form.organization_id, contractor_id: cid,
      }),
      enabled: !!form.organization_id && !!cid,
    })),
  });
  const contractsByContractor = useMemo(() => {
    const m: Record<string, { id: string; code: string; name: string }[]> = {};
    contractorIdsInUse.forEach((cid, idx) => {
      const q = contractQueries[idx];
      if (q?.data) m[cid] = q.data;
    });
    return m;
  }, [contractorIdsInUse, contractQueries]);

  // Опції субконто за типом (клієнтський SearchSelect).
  const subcontoOptions = useMemo(() => {
    const m: Record<string, { id: string; label: string }[]> = {
      'ref.contractors': (ctrQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name })),
      'ref.nomenclature': (nomQ.data?.items ?? []).map((n) => ({ id: n.id, label: n.name })),
      'ref.employees': (empQ.data ?? []).map((e) => ({
        id: e.id, label: e.code ? `${e.name} — ${e.code}` : e.name,
      })),
      'ref.warehouses': (whQ.data ?? []).map((w) => ({ id: w.id, label: w.name })),
      'ref.cost_items': (ciQ.data ?? []).map((c) => ({ id: c.id, label: c.name })),
    };
    return m;
  }, [ctrQ.data, nomQ.data, empQ.data, whQ.data, ciQ.data]);

  // Значення для слота «Договір»: договори контрагента того ж боку.
  function contractOptions(e: ManualOperationEntry, side: Side) {
    let contractorId: string | null = null;
    ([1, 2, 3] as SlotN[]).forEach((n) => {
      if (e[slotKey(side, n, 'type')] === 'ref.contractors') {
        contractorId = (e[slotKey(side, n, 'id')] as string | null) ?? null;
      }
    });
    if (!contractorId) return [];
    return (contractsByContractor[contractorId] ?? []).map((c) => ({
      id: c.id,
      label: c.name ? `${c.name}${c.code ? ` (${c.code})` : ''}` : c.code,
    }));
  }

  // ---- Типові операції (шаблони) -----------------------------------------
  const tplQ = useQuery({
    queryKey: ['manual-op-templates'],
    queryFn: () => manualOpTemplates.list(),
  });
  const [tplId, setTplId] = useState('');
  const [tplNameDraft, setTplNameDraft] = useState<string | null>(null);
  const tplSave = useMutation({
    mutationFn: (name: string) => manualOpTemplates.upsert({
      name,
      content: form.operation_content || null,
      entries: form.entries.map(({ id: _id, ...e }) => ({ ...e, amount: '0', tax_amount: null })),
    }),
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ['manual-op-templates'] });
      setTplNameDraft(null);
      setTplId(tpl.id);
    },
    onError: (e) => setError(extractError(e)),
  });
  const tplRemove = useMutation({
    mutationFn: (rid: string) => manualOpTemplates.remove(rid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-op-templates'] });
      setTplId('');
    },
    onError: (e) => setError(extractError(e)),
  });
  function applyTemplate() {
    const tpl = (tplQ.data ?? []).find((x) => x.id === tplId);
    if (!tpl) return;
    setForm((f) => ({
      ...f,
      operation_content: f.operation_content || (tpl.content ?? ''),
      entries: tpl.entries.length
        ? tpl.entries.map((e, i) => ({
            ...NEW_ROW(i + 1), ...e, line_number: i + 1,
            amount: '0', tax_amount: null,
          }))
        : f.entries,
    }));
  }

  useEffect(() => {
    if (isNew && !copy.source) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (copy.source ?? docQuery.data) {
      const d = (copy.source ?? docQuery.data)!;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        operation_content: d.operation_content ?? '',
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        entries: d.entries.length
          ? d.entries.map((e) => ({
              ...e,
              amount: String(e.amount),
              tax_amount: e.tax_amount != null ? String(e.tax_amount) : null,
              dt_quantity: e.dt_quantity != null ? String(e.dt_quantity) : null,
              cr_quantity: e.cr_quantity != null ? String(e.cr_quantity) : null,
              dt_currency_amount: e.dt_currency_amount != null ? String(e.dt_currency_amount) : null,
              cr_currency_amount: e.cr_currency_amount != null ? String(e.cr_currency_amount) : null,
              content: e.content ?? '',
            }))
          : EMPTY_FORM.entries,
        operation_subtype: ((d as { operation_subtype?: string }).operation_subtype as 'batch_correction' | '' | undefined) ?? '',
        related_batch_key_id: (d as { related_batch_key_id?: string }).related_batch_key_id ?? '',
        new_cost_price: (d as { new_cost_price?: string }).new_cost_price ?? '',
      });
      if (copy.source) {
        // копія — новий документ: свій номер і дата, без привʼязок зразка
        setForm((f) => ({ ...f, document_number: EMPTY_FORM.document_number, document_date: nowForDocDate(), }));
        copy.done();
      }
    }
  }, [isNew, docQuery.data, copy.source, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const totalAmount = useMemo(
    () => form.entries.reduce((s, e) => s + Number(e.amount || 0), 0),
    [form.entries],
  );

  const createPayload = () => ({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      operation_content: form.operation_content || null,
      comment: form.comment || null,
      project_id: form.project_id,
      entries: cleanEntries(form.entries),
      // Фаза 2.1 #3: subtype + batch correction поля.
      operation_subtype: form.operation_subtype || null,
      related_batch_key_id: form.related_batch_key_id || null,
      new_cost_price: form.new_cost_price || null,
    } as unknown as Parameters<typeof manualOps.create>[0]);
  const create = useMutation({
    mutationFn: () => manualOps.create(createPayload()),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
      navigate(`/manual-operations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const updatePayload = () => ({
    document_number: form.document_number,
    document_date: new Date(form.document_date).toISOString(),
    operation_content: form.operation_content || null,
    comment: form.comment || null,
    project_id: form.project_id,
    entries: cleanEntries(form.entries),
  });
  const update = useMutation({
    mutationFn: () => manualOps.update(id!, updatePayload()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['manual-op', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    // ч.1266: на новому документі — створити, провести, відкрити створений.
    // ч.1267: на наявному — спершу зберегти правки форми (змінив проводки → «провести»
    // проводило СТАРИЙ збережений вміст, правки губились мовчки).
    mutationFn: async () => {
      if (isNew) {
        return createThenPost(
          () => manualOps.create(createPayload()),
          (docId) => manualOps.post(docId),
          (doc) => { qc.invalidateQueries({ queryKey: ['manual-ops'] }); navigate(`/manual-operations/${doc.id}`); },
        );
      }
      if (!isPosted) await manualOps.update(id!, updatePayload());
      return manualOps.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-op', id] });
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => manualOps.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-op', id] });
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => manualOps.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manual-ops'] });
      navigate('/manual-operations');
    },
  });

  function setEntry(i: number, patch: Partial<ManualOperationEntry>) {
    setForm((f) => ({
      ...f,
      entries: f.entries.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  // Зміна рахунку: підставити типи субконто за підказкою + скинути значення.
  function setAccount(i: number, side: Side, accountId: string) {
    const code = accountById[accountId]?.code;
    const hints = hintForAccount(code);
    const patch: Partial<ManualOperationEntry> = {
      [`${side}_account_id`]: accountId,
    } as Partial<ManualOperationEntry>;
    ([1, 2, 3] as SlotN[]).forEach((n) => {
      (patch as Record<string, string | null>)[slotKey(side, n, 'type')] = hints[n - 1] ?? null;
      (patch as Record<string, string | null>)[slotKey(side, n, 'id')] = null;
    });
    const acc = accountById[accountId];
    if (!acc?.use_quantity) (patch as Record<string, string | null>)[`${side}_quantity`] = null;
    if (!acc?.use_currency) {
      (patch as Record<string, string | null>)[`${side}_currency_id`] = null;
      (patch as Record<string, string | null>)[`${side}_currency_amount`] = null;
    }
    setEntry(i, patch);
  }
  function addRow() {
    setForm((f) => ({ ...f, entries: [...f.entries, NEW_ROW(f.entries.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      entries: f.entries
        .filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // Слоти субконто одного боку: показуємо ті, де є тип, + один порожній.
  function visibleSlots(e: ManualOperationEntry, side: Side): SlotN[] {
    const out: SlotN[] = [];
    let shownEmpty = false;
    for (const n of [1, 2, 3] as SlotN[]) {
      const hasType = !!e[slotKey(side, n, 'type')];
      if (hasType) { out.push(n); continue; }
      if (!shownEmpty && !isPosted && e[`${side}_account_id`]) {
        out.push(n); shownEmpty = true;
      }
    }
    return out;
  }

  function renderSide(e: ManualOperationEntry, i: number, side: Side) {
    const accId = e[`${side}_account_id`];
    const acc = accId ? accountById[accId] : undefined;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        <div style={{ width: 130 }} title={acc ? `${acc.code} — ${acc.name}` : undefined}>
          <SearchSelect
            items={accountOptions}
            value={accId}
            disabled={isPosted}
            placeholder="рахунок"
            onChange={(v) => setAccount(i, side, v)}
          />
        </div>
        {visibleSlots(e, side).map((n) => {
          const typeVal = (e[slotKey(side, n, 'type')] as string | null) ?? '';
          const idVal = (e[slotKey(side, n, 'id')] as string | null) ?? '';
          const opts = typeVal === 'ref.contracts'
            ? contractOptions(e, side)
            : (subcontoOptions[typeVal] ?? []);
          return (
            <div key={n} style={{ display: 'flex', gap: '0.3rem' }}>
              <select
                value={typeVal}
                disabled={isPosted}
                style={{ width: 130, flexShrink: 0, fontSize: '0.85rem' }}
                onChange={(ev) => setEntry(i, {
                  [slotKey(side, n, 'type')]: ev.target.value || null,
                  [slotKey(side, n, 'id')]: null,
                } as Partial<ManualOperationEntry>)}
              >
                <option value="">— субконто —</option>
                {SUBCONTO_TYPES.map((s) => (
                  <option key={s.type} value={s.type}>{s.label}</option>
                ))}
              </select>
              {typeVal && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchSelect
                    items={opts}
                    value={idVal}
                    disabled={isPosted}
                    placeholder={typeVal === 'ref.contracts' && !opts.length
                      ? 'спершу оберіть контрагента' : '— оберіть —'}
                    onChange={(v) => setEntry(i, {
                      [slotKey(side, n, 'id')]: v || null,
                    } as Partial<ManualOperationEntry>)}
                  />
                </div>
              )}
            </div>
          );
        })}
        {(acc?.use_quantity || acc?.use_currency) && (
          <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
            {acc?.use_quantity && (
              <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                к-сть
                <input
                  type="number" step="0.0001" min="0"
                  style={{ width: 90 }}
                  value={e[`${side}_quantity`] ?? ''}
                  disabled={isPosted}
                  onChange={(ev) => setEntry(i, {
                    [`${side}_quantity`]: ev.target.value || null,
                  } as Partial<ManualOperationEntry>)}
                />
              </label>
            )}
            {acc?.use_currency && (
              <>
                <select
                  style={{ width: 80, fontSize: '0.85rem' }}
                  value={e[`${side}_currency_id`] ?? ''}
                  disabled={isPosted}
                  onChange={(ev) => setEntry(i, {
                    [`${side}_currency_id`]: ev.target.value || null,
                  } as Partial<ManualOperationEntry>)}
                >
                  <option value="">вал.</option>
                  {(curQ.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>{c.code}</option>
                  ))}
                </select>
                <input
                  type="number" step="0.01"
                  placeholder="вал. сума"
                  style={{ width: 110 }}
                  value={e[`${side}_currency_amount`] ?? ''}
                  disabled={isPosted}
                  onChange={(ev) => setEntry(i, {
                    [`${side}_currency_amount`]: ev.target.value || null,
                  } as Partial<ManualOperationEntry>)}
                />
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати ручну операцію у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/manual-operations')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.917 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const isBatch = form.operation_subtype === 'batch_correction';
  // ч.917: обовʼязкові поля блоків — за умовами проведення (postDisabled) та позначками <Req />.
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    isBatch
      ? { id: 'tables', title: 'Коригування партії', required: 2,
          filled: (form.related_batch_key_id ? 1 : 0) + (form.new_cost_price ? 1 : 0) }
      : { id: 'tables', title: 'Проводки', required: 1,
          filled: form.entries.some((e) => e.dt_account_id && e.cr_account_id) ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  // ч.917: дії ТЧ «Проводки» — у шапці блоку (заповнення з шаблону + додати рядок).
  const tableActions = isBatch ? undefined : (
    <>
      <button type="button" className="btn-doc" disabled={!tplId || isPosted} onClick={applyTemplate}
        title={!tplId ? 'Спершу оберіть типову операцію' : undefined}
        style={{ whiteSpace: 'nowrap' }}>сформувати проводки</button>
      {!isPosted && (
        <button type="button" className="primary" onClick={addRow}>+ {t('mo.add_row')}</button>
      )}
    </>
  );

  return (
    <div className="card">
      {/* ч.917: шапка — один рядок (назва ліворуч, дії праворуч). */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? t('mo.new') : `${t('mo.title')}: ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
        entriesDoc={{ type: 'doc.manual_operations', id }}
          railMode
          extraActions={railActions}
          onBackToList={() => navigate('/manual-operations')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => post.mutate()}
          postDisabled={form.operation_subtype === 'batch_correction'
            ? !(form.related_batch_key_id && form.new_cost_price)
            : !form.entries.some((e) => e.dt_account_id && e.cr_account_id)}
          postPending={post.isPending}
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }) }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/manual-operations/${id}/print`)}
        />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={[...railActions, copyRailAction(isNew, () => navigate(`/manual-operations/new?copy=${id}`))]} isNew={isNew}
          doc={{ type: 'doc.manual_operations', id: isNew ? '' : id! }} />
      }>
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('mo.organization')}<Req /></label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('mo.number')}</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>{t('mo.date')}<Req /></label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
        <ProjectField span={3} label="Проект / напрям"
            value={form.project_id}
            organizationId={form.organization_id}
            onChange={(v) => setForm({ ...form, project_id: v })}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title={isBatch ? 'Коригування партії' : 'Проводки'} actions={tableActions}>
      {/* Дві вкладки (рішення власника 2026-08-23): «Проводки» — ручна ТЧ;
          «Коригування партії» — переоцінка партії (ТЧ не використовується).
          Взаємовиключні режими; перемикач = operation_subtype. */}
      <div className="seg-tabs" style={{ marginBottom: '0.5rem' }}>
        <button type="button" className={form.operation_subtype !== 'batch_correction' ? 'active' : ''}
          disabled={isPosted || !isNew}
          onClick={() => setForm({ ...form, operation_subtype: '', related_batch_key_id: '', new_cost_price: '' })}>
          Проводки
        </button>
        <button type="button" className={form.operation_subtype === 'batch_correction' ? 'active' : ''}
          disabled={isPosted || !isNew}
          onClick={() => setForm({ ...form, operation_subtype: 'batch_correction' })}>
          Коригування собівартості партії
        </button>
      </div>
      {form.operation_subtype === 'batch_correction' ? (
        <div>
          <div className="field">
            <label>{t('mo.content')}</label>
            <input
              value={form.operation_content}
              onChange={(e) => setForm({ ...form, operation_content: e.target.value })}
              disabled={isPosted}
            />
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', margin: '0.4rem 0 0.2rem' }}>
            Переоцінка партії запасів: оберіть партію і нову собівартість — проводки
            Дт 281 — Кт 281 на різницю та перерахунок рухів після дати документа система
            виконає при проведенні.
          </div>
          <div style={{ marginTop: '0.5rem', display: 'grid',
                        gridTemplateColumns: '1fr 1.5fr 1.5fr 160px', gap: '0.5rem', alignItems: 'end' }}>
            <div className="field">
              <label>Склад<Req /></label>
              <select value={bcWarehouse} disabled={isPosted}
                onChange={(e) => { setBcWarehouse(e.target.value); setBcNom(''); setForm({ ...form, related_batch_key_id: '' }); }}>
                <option value="">— оберіть —</option>
                {bcWarehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Номенклатура<Req /></label>
              <SearchSelect
                items={bcNoms}
                value={bcNom}
                disabled={isPosted || !bcWarehouse}
                placeholder={bcWarehouse ? '— оберіть —' : 'спершу склад'}
                onChange={(v) => { setBcNom(v); setForm({ ...form, related_batch_key_id: '' }); }}
              />
            </div>
            <div className="field">
              <label>Партія (надходження)<Req /></label>
              <select value={form.related_batch_key_id} disabled={isPosted || !bcNom}
                onChange={(e) => setForm({ ...form, related_batch_key_id: e.target.value })}>
                <option value="">— оберіть —</option>
                {bcBatches.map((b) => (
                  <option key={b.batch_key_id} value={b.batch_key_id}>
                    {new Date(b.receipt_date).toLocaleDateString('uk-UA')} · {DOC_TABLE_NAMES_UK[b.receipt_document_type] ?? b.receipt_document_type}
                    {' '}· залишок {Number(b.quantity).toLocaleString('uk-UA')} · собівартість {Number(b.cost_price).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Нова собівартість<Req /></label>
              <input
                type="number" step="0.0001"
                value={form.new_cost_price}
                disabled={isPosted}
                onChange={(e) => setForm({ ...form, new_cost_price: e.target.value })}
              />
            </div>
            {bcSelected && (
              <div style={{ gridColumn: 'span 4', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Поточна собівартість партії: <strong>{Number(bcSelected.cost_price).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</strong>,
                залишок {Number(bcSelected.quantity).toLocaleString('uk-UA')} од. — при проведенні партія переоцінюється,
                рухи після дати документа перераховуються.
              </div>
            )}
          </div>
        </div>
      ) : (<>
      {/* Зміст операції + типова операція (шаблон) в одному рядку.
          ч.917: кнопка «сформувати проводки» — у шапці блоку. */}
      <div className="doc-grid" style={{ alignItems: 'end' }}>
        <div className="field" style={{ gridColumn: 'span 8' }}>
          <label>{t('mo.content')}</label>
          <input
            value={form.operation_content}
            onChange={(e) => setForm({ ...form, operation_content: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Типова операція</label>
          <select
            value={tplId}
            onChange={(e) => setTplId(e.target.value)}
            disabled={isPosted}
          >
            <option value="">— оберіть шаблон —</option>
            {(tplQ.data ?? []).map((tp) => (
              <option key={tp.id} value={tp.id}>{tp.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', marginBottom: '0.4rem' }}>
        {!isPosted && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {tplNameDraft === null ? (
              <button onClick={() => setTplNameDraft(form.operation_content || '')}
                style={{ whiteSpace: 'nowrap' }}>
                зберегти рядки як шаблон
              </button>
            ) : (
              <>
                <input
                  placeholder="назва шаблону"
                  value={tplNameDraft}
                  onChange={(e) => setTplNameDraft(e.target.value)}
                  style={{ width: 220 }}
                />
                <button
                  disabled={!tplNameDraft.trim() || tplSave.isPending}
                  onClick={() => tplSave.mutate(tplNameDraft.trim())}
                >зберегти</button>
                <button onClick={() => setTplNameDraft(null)}>скасувати</button>
              </>
            )}
            {tplId && (
              <button
                className="danger"
                disabled={tplRemove.isPending}
                onClick={() => {
                  confirm('Видалити шаблон?').then((ok) => { if (ok) tplRemove.mutate(tplId); });
                }}
                style={{ whiteSpace: 'nowrap' }}
              >видалити шаблон</button>
            )}
          </div>
        )}
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 28 }}>#</th>
            <th>{t('mo.dt')}<Req /></th>
            <th>{t('mo.cr')}<Req /></th>
            <th style={{ width: 130 }}>{t('mo.amount')}<Req /></th>
            <th>{t('mo.entry_content')}</th>
            <th style={{ width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.entries.map((e, i) => (
            <tr key={i}>
              <td>{e.line_number}</td>
              <td style={{ minWidth: 200 }}>
                {renderSide(e, i, 'dt')}
              </td>
              <td style={{ minWidth: 200 }}>
                {renderSide(e, i, 'cr')}
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={e.amount}
                  onChange={(ev) => setEntry(i, { amount: ev.target.value })}
                  disabled={isPosted}
                />
                <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  н/о
                  <input
                    type="number" step="0.01"
                    value={e.tax_amount ?? ''}
                    placeholder="сума ПО"
                    onChange={(ev) => setEntry(i, { tax_amount: ev.target.value || null })}
                    disabled={isPosted}
                  />
                </label>
              </td>
              <td>
                <input
                  value={e.content ?? ''}
                  onChange={(ev) => setEntry(i, { content: ev.target.value })}
                  disabled={isPosted}
                />
              </td>
              <td>
                {!isPosted && form.entries.length > 1 && (
                  <button onClick={() => removeRow(i)} className="danger">×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} style={{ textAlign: 'right' }}>{t('mo.total')}:</td>
            <td>{totalAmount.toFixed(2)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </TchTable>
      {/* ч.917: підсумок лишається з ТЧ; коментар — в останньому блоці. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
        <div className="field" style={{ width: 220 }}>
          <label>Сума операції</label>
          <input value={totalAmount.toFixed(2)} disabled readOnly />
        </div>
      </div>

      </>)}
      </DocBlock>

      <DocBlock index={3} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={isPosted}
          />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.manual_operations" documentId={isNew ? undefined : id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
      {isPosted && !isNew && (
        <FopExclusionRescueSection
          organizationId={form.organization_id}
          documentId={id!}
        />
      )}
    </div>
  );
}

// Порожні строки числових полів → null (Pydantic Decimal не приймає '').
function cleanEntries(entries: ManualOperationEntry[]): ManualOperationEntry[] {
  return entries.map((e) => ({
    ...e,
    amount: e.amount || '0',
    tax_amount: e.tax_amount || null,
    dt_quantity: e.dt_quantity || null,
    cr_quantity: e.cr_quantity || null,
    dt_currency_amount: e.dt_currency_amount || null,
    cr_currency_amount: e.cr_currency_amount || null,
    dt_currency_id: e.dt_currency_id || null,
    cr_currency_id: e.cr_currency_id || null,
  }));
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
